import { useContext, useMemo } from "react"
import { alpha, Box, styled } from "@mui/material"
import { ResponseDeclaration } from "../../models"
import {
  FIELD_CHOICE_ATTRIBUTES,
  FIELD_CHOICE_CHECKED_ATTRIBUTES
} from "../../constants"
import { onKeyboardSubmit } from "../../helpers"
import { ReviewIcon } from "../../ui/icons"
import { HotTextScoringContext } from "../HotTextInteraction/HotTextScoringContext"
import { focusStyle } from "../../theme"
import {
  CheckCorrect,
  isQuestionComplete,
  NeutralSetting,
  Variant
} from "../../types"
// import { TooltipFeedbackGroupProps } from '@app/components/feedback/TooltipFeedbackGroup'

export interface HotTextComponentProps {
  identifier: string
  templateidentifier?: string
  minChoices?: number
  maxChoices?: number
  responseDeclaration?: ResponseDeclaration
  onSelect: (id: string) => void
  selectedChoices: string[]
  isValid: boolean
  accessibilityAttr?: { [key: string]: string }
  checkCorrect?: CheckCorrect
  isCompletedQuestion?: isQuestionComplete
  isNeutralSetting?: NeutralSetting
}

export const HotTextComponent: React.FC<
  React.PropsWithChildren<HotTextComponentProps>
> = props => {
  const {
    identifier,
    children,
    selectedChoices,
    onSelect,
    accessibilityAttr,
    isCompletedQuestion,
    checkCorrect,
    isNeutralSetting
  } = props
  const { questionCompleted } = isCompletedQuestion?.() ?? {
    questionCompleted: false
  }

  const selected = useMemo(
    () =>
      (selectedChoices && selectedChoices?.includes(identifier)) ||
      (questionCompleted && !isNeutralSetting && checkCorrect?.(identifier)),
    [
      identifier,
      selectedChoices,
      questionCompleted,
      checkCorrect,
      isNeutralSetting
    ]
  )
  const { correctResponses, groupMode } = useContext(HotTextScoringContext)
  const correct = correctResponses?.includes(identifier)
  // const feedbackItems = groupMode
  //   ? getElementFeedbackItems(
  //       createElement('span', { identifier, children }),
  //       groupStatistics,
  //       correctResponses,
  //     )
  //   : undefined

  return (
    <HotText
      identifier={identifier}
      selected={groupMode ? correctResponses?.includes(identifier) : selected}
      onSelect={onSelect}
      correct={correct}
      groupMode={groupMode}
      questionCompleted={questionCompleted}
      checkCorrect={checkCorrect}
      // feedbackItems={feedbackItems}
      accessibilityAttr={accessibilityAttr}
      isNeutralSetting={isNeutralSetting}
    >
      {children}
    </HotText>
  )
}

export interface HotTextProps {
  identifier: string
  onSelect: (id: string) => void
  selected?: boolean
  correct?: boolean
  groupMode?: boolean
  questionCompleted?: boolean
  checkCorrect?: CheckCorrect
  // feedbackItems?: TooltipFeedbackGroupProps['items']
  accessibilityAttr?: Record<string, string>
  isNeutralSetting?: NeutralSetting
}

export const HotText: React.FC<React.PropsWithChildren<HotTextProps>> = ({
  identifier,
  selected = false,
  onSelect,
  correct,
  groupMode = false,
  questionCompleted,
  checkCorrect,
  // feedbackItems,
  accessibilityAttr,
  children,
  isNeutralSetting
}) => {
  const badgeClass: { variant: Variant | null } = useMemo(() => {
    if (selected && isNeutralSetting) {
      return { variant: Variant.neutral }
    } else if (selected && checkCorrect) {
      return checkCorrect?.(identifier)
        ? { variant: Variant.correct }
        : { variant: Variant.invalid }
    }
    return { variant: null }
  }, [selected, identifier, checkCorrect, isNeutralSetting])

  const isEmpty =
    Array.isArray(children) &&
    children.every(child => typeof child === "string" && child.trim() === "")

  return (
    <StyledBox
      role="checkbox"
      tabindex="0"
      aria-checked={selected}
      component={"span"}
      selected={selected}
      disabled={questionCompleted}
      isEmpty={isEmpty}
      onClick={() => !questionCompleted && onSelect(identifier)}
      onKeyDown={onKeyboardSubmit(
        () => !questionCompleted && onSelect(identifier)
      )}
      {...FIELD_CHOICE_ATTRIBUTES}
      {...(selected ? FIELD_CHOICE_CHECKED_ATTRIBUTES : {})}
      {...accessibilityAttr}
      {...badgeClass}
    >
      {/* {(correct !== undefined || groupMode) && (
        <StyledIconContainer>
          <ReviewIcon correct={correct} groupMode={groupMode} items={feedbackItems} />
        </StyledIconContainer>
      )} */}
      {(correct !== undefined || badgeClass.variant) &&
        !groupMode &&
        !isNeutralSetting && (
          <StyledIconContainer>
            <ReviewIcon
              correct={correct || badgeClass.variant === Variant.correct}
            />
          </StyledIconContainer>
        )}
      {/* Add a non-breaking space if the content is empty */}
      {isEmpty ? "\u00A0" : children}
    </StyledBox>
  )
}

const StyledIconContainer = styled(Box)(({ theme }) => ({
  margin: theme.spacing(0.5),
  verticalAlign: "top",
  display: "inline-flex",
  alignItems: "center"
}))

interface StyledBoxProps {
  selected: boolean
  tabindex: string
  variant?: Variant
  disabled?: boolean
  isEmpty?: boolean
}

const StyledBox = styled(Box)<StyledBoxProps>(
  ({ theme, selected, variant, disabled, isEmpty }) => ({
    boxSizing: "border-box",
    borderBottom: `2px solid ${theme.palette.blue[400]}`,
    borderRadius: theme.shape.borderRadius,
    backgroundColor: alpha(theme.palette.blue[50], 0.5),
    lineHeight: 1.3,
    cursor: "pointer",
    padding: theme.spacing(0, 1),
    display: "inline", // Use inline to allow text wrapping
    alignItems: "center",
    gap: theme.spacing(1),

    ...(isEmpty && {
      minWidth: theme.spacing(10)
    }),

    ...(!variant &&
      !disabled && {
        "&:hover": {
          [theme.breakpoints.up("mobile")]: {
            color: theme.palette.grey[800],
            borderBottom: `2px solid ${theme.palette.blue[300]}`,
            backgroundColor: theme.palette.blue[100]
          }
        }
      }),

    "&:focus": {
      [theme.breakpoints.up("mobile")]: {},
      border: `2px solid ${theme.palette["blue"][500]}`,
      backgroundColor: `${theme.palette["blue"][100]}`
    },

    ...(selected &&
      !variant && {
        backgroundColor: theme.palette.blue[600],
        color: theme.palette.common.white,
        borderColor: "transparent"
      }),

    ...(variant && {
      borderBottom: `2px solid
        ${
          theme.palette[
            variant === Variant.neutral
              ? "blue"
              : variant === Variant.correct
              ? "lightGreen"
              : "orange"
          ][variant === Variant.neutral ? "A800" : 700]
        }`,
      backgroundColor: `${
        theme.palette[
          variant === Variant.neutral
            ? "blue"
            : variant === Variant.correct
            ? "lightGreen"
            : "orange"
        ][variant === Variant.neutral ? 100 : 50]
      }`
    })
  })
)
